import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

// This will be replaced by actual get_form_url call at component mount
let contactFormUrl = 'https://readdy.ai/api/form/d9otin0senbniolesln0';

function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formLoaded, setFormLoaded] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const faqs = [
    {
      q: '韩漫漫画网上的漫画是免费的吗？',
      a: '是的，韩漫漫画网上的所有漫画目前均提供免费在线阅读。我们致力于为广大漫画爱好者提供最便捷、最经济的阅读体验。部分特殊内容可能会根据版权方要求调整，请以实际页面显示为准。',
    },
    {
      q: '漫画更新频率是怎样的？',
      a: '韩漫漫画网与韩国漫画平台保持同步更新，热门连载作品通常每周更新一话。我们的编辑团队全天候监控更新动态，确保在第一时间为您带来最新内容。具体更新日期可在每部漫画的详情页查看。',
    },
    {
      q: '可以在手机上阅读吗？',
      a: '当然可以！韩漫漫画网采用响应式设计，完美适配手机、平板、电脑等多种设备。在手机上阅读时，我们还特别优化了竖屏滚动体验，让您随时随地畅享韩漫阅读乐趣。',
    },
    {
      q: '如何找到我想看的漫画？',
      a: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用顶部的搜索功能直接搜索漫画名称或作者。在分类页面中，您还可以按评分、人气、更新时间等维度排序，快速找到心仪的作品。',
    },
    {
      q: '遇到阅读问题怎么办？',
      a: '如遇到加载缓慢、图片无法显示等问题，建议先检查网络连接或刷新页面。如问题持续存在，请通过本页面的联系表单或发送邮件至 contact@hanman.com 与我们联系，我们会在24小时内为您解决。',
    },
    {
      q: '韩漫漫画网的漫画来源是哪里？',
      a: '韩漫漫画网与多家韩国知名漫画平台和出版社建立了合作关系，所有漫画均获得正版授权。我们尊重每一位创作者的版权，也感谢广大读者对正版漫画的支持。',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫漫画网',
    description: '韩漫漫画网是热门漫画在线阅读平台，实现全网韩漫最快更新，涵盖韩漫大全，内容多样，加载快捷，为您提供极速阅读服务。',
    url: 'https://hanman.com/about',
  };

  const testimonials = [
    {
      name: '小雨',
      avatar: 'ri-user-3-line',
      role: '韩漫爱好者',
      content: '自从发现了韩漫漫画网，我每天都会来看更新！更新速度真的太快了，画质也很清晰，阅读体验一级棒。强烈推荐给所有喜欢韩漫的朋友！',
      rating: 5,
    },
    {
      name: '阿诚',
      avatar: 'ri-user-3-line',
      role: '资深读者',
      content: '用了很多漫画平台，韩漫漫画网是更新最快的。特别是热门漫画，基本和韩国同步，再也不用等翻译了。收藏功能也很方便。',
      rating: 5,
    },
    {
      name: '小美',
      avatar: 'ri-user-3-line',
      role: '漫画博主',
      content: '作为一个漫画博主，我经常需要第一时间了解最新的韩漫动态。韩漫漫画网帮了我大忙，海量资源加上超快更新，做内容再也不愁素材了。',
      rating: 5,
    },
    {
      name: '大海',
      avatar: 'ri-user-3-line',
      role: '上班族',
      content: '通勤路上全靠韩漫解闷，韩漫漫画网的手机体验真的不错，加载很快，翻页也顺滑。分类也很细致，总能找到喜欢的类型。',
      rating: 5,
    },
    {
      name: '莉莉',
      avatar: 'ri-user-3-line',
      role: '学生党',
      content: '韩漫漫画网陪伴了我整个大学生活！恋爱漫画甜到齁，剧情漫画也很精彩。喜欢漫画的一定要试试这个平台，不会让你失望的。',
      rating: 5,
    },
    {
      name: 'Jack',
      avatar: 'ri-user-3-line',
      role: '设计师',
      content: '作为一个设计从业者，我很欣赏韩漫漫画网的UI设计——简洁大气，用起来非常顺手。当然，漫画内容本身也很棒，推荐！',
      rating: 5,
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫漫画网"
        description="了解韩漫漫画网品牌故事、发展历程与核心优势。韩漫漫画网是热门韩漫在线阅读平台，实现全网最快更新，为广大漫画爱好者提供极速阅读服务。"
        keywords="韩漫漫画网,关于我们,韩漫平台,品牌介绍,联系我们"
        canonicalUrl="/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <main className="w-full bg-background-50 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-4" style={{ fontFamily: 'var(--font-heading)' }}>
              关于韩漫漫画网
            </h1>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <section className="mb-12" aria-labelledby="brand-title">
            <h2 id="brand-title" className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              品牌介绍
            </h2>
            <div className="p-6 rounded-xl bg-background-100">
              <p className="text-foreground-700 leading-relaxed mb-4">
                韩漫漫画网成立于2024年，是国内领先的韩漫在线阅读平台。我们致力于为广大华语读者提供最优质、最快速的韩漫阅读体验。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                平台与多家韩国知名漫画平台和出版社建立了深度合作关系，拥有海量正版韩漫资源。从恋爱甜宠到热血动作，从悬疑推理到奇幻冒险，韩漫漫画网涵盖了几乎所有热门韩漫题材，满足不同读者的多样化需求。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                我们的核心竞争力在于"快"——通过先进的内容分发网络和专业的翻译团队，韩漫漫画网实现了与韩国同步更新的速度，让华语读者无需漫长等待，第一时间享受最新韩漫内容。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                此外，我们持续投入技术研发，优化阅读体验。智能预加载、自适应排版、多端同步等功能，都是为了一个目标：让每一位读者都能享受到流畅、舒适、沉浸的在线阅读体验。
              </p>
            </div>
          </section>

          <section className="mb-12" aria-labelledby="values-title">
            <h2 id="values-title" className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: 'ri-speed-line', title: '极速更新', desc: '与韩国同步更新，第一时间带来最新漫画内容，拒绝等待。' },
                { icon: 'ri-shield-check-line', title: '正版授权', desc: '所有漫画均获得正版授权，尊重创作者版权，支持原创。' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，不断优化产品，提供最好的阅读服务。' },
                { icon: 'ri-global-line', title: '开放共享', desc: '让全球华语读者都能便捷地享受韩漫文化带来的乐趣。' },
                { icon: 'ri-brush-line', title: '品质为先', desc: '高清画质、精准翻译、精致排版，每一个细节都力求完美。' },
                { icon: 'ri-team-line', title: '团队协作', desc: '编辑、翻译、技术团队紧密合作，高效运作保障平台稳定。' },
              ].map((v, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className={`${v.icon} text-lg text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-1.5">{v.title}</h3>
                  <p className="text-sm text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12" aria-labelledby="testimonials-title">
            <h2 id="testimonials-title" className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              读者口碑
            </h2>
            <p className="text-sm text-foreground-600 mb-6">来自百万读者的真实评价，是我们不断前行的动力。</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10">
                      <i className={`${t.avatar} text-lg text-primary-500`}></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12" aria-labelledby="contact-title">
            <h2 id="contact-title" className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-background-100">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">电子邮箱</div>
                      <a href="mailto:contact@hanman.com" className="text-sm font-medium text-foreground-950 hover:text-primary-500 transition-colors cursor-pointer">contact@hanman.com</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">客服电话</div>
                      <span className="text-sm font-medium text-foreground-950">400-888-9999</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">公司地址</div>
                      <span className="text-sm font-medium text-foreground-950">北京市朝阳区望京SOHO T3-B座 1806室</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">工作时间</div>
                      <span className="text-sm font-medium text-foreground-950">周一至周五 9:00 - 18:00</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-100">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form
                  className="flex flex-col gap-3"
                  data-readdy-form
                  action={contactFormUrl || '#'}
                  method="POST"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!contactFormUrl) {
                      setFormError('表单尚未初始化，请稍后再试');
                      setFormStatus('error');
                      return;
                    }

                    const form = e.currentTarget;
                    const formData = new FormData(form);

                    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
                    if (honeypotValue) {
                      setFormStatus('success');
                      form.reset();
                      return;
                    }

                    setFormStatus('idle');
                    try {
                      const params = new URLSearchParams();
                      formData.forEach((value, key) => {
                        if (key !== 'phone_alt') {
                          params.append(key, value as string);
                        }
                      });

                      const response = await fetch(contactFormUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: params.toString(),
                      });

                      const responseText = await response.text();
                      let parsed;
                      try {
                        parsed = JSON.parse(responseText);
                      } catch {
                        parsed = null;
                      }

                      if (response.ok && parsed?.code === 'OK') {
                        setFormStatus('success');
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
                        setFormError(serverMsg);
                        setFormStatus('error');
                      }
                    } catch {
                      setFormError('网络错误，请检查连接后重试');
                      setFormStatus('error');
                    }
                  }}
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的留言..."
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center mt-2">消息已发送成功！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center mt-2">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          <section className="mb-8" aria-labelledby="faq-title">
            <h2 id="faq-title" className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="flex flex-col gap-2">
              {faqs.map((faq, idx) => (
                <div key={idx} className="rounded-xl bg-background-100 overflow-hidden">
                  <button
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                    aria-expanded={activeFaq === idx}
                  >
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</span>
                    <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${activeFaq === idx ? 'rotate-180' : ''}`}></i>
                  </button>
                  {activeFaq === idx && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-700 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}

export default AboutPage;